import { useEffect, useMemo } from "react";
import { Button, Card, CardBody, Chip, Skeleton } from "@heroui/react";
import { Link } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import {
    IRRIGATION_FILL_LAYER,
    IRRIGATION_LINE_LAYER,
    IRRIGATION_SOURCE,
} from "@/lib/map/layers";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    moveLayersToTop,
    updateLayerVisibility,
} from "@/lib/utils/map";
import { useI18n } from "@/lib/i18n/useI18n";
import { getPumpName } from "@/lib/utils/pump";
import {
    IRRIGATION_SYSTEM_TYPE_I18N,
    IrrigationSystem,
    IrrigationSystemType,
} from "@/types/irrigationSystem";

function getSystemDisplayName(
    system: IrrigationSystem,
    index: number,
    t: (key: string, options?: Record<string, unknown>) => string,
) {
    const name = system.name?.trim();
    if (name) return name;
    return t("irrigation_system.unnamed", {
        ns: "setup",
        number: index + 1,
    });
}

function IrrigationSystems() {
    const { t } = useTranslation();
    const { formatUnit } = useI18n();
    const { map, mapLoaded } = useMap();
    const { fieldId, fieldQuery, geometryQuery } = useFieldDashboard() ?? {};
    const irrigationSystems = useMemo(() => {
        return fieldQuery?.data?.irrigation_systems ?? [];
    }, [fieldQuery?.data]);

    const irrigationBoundaries = useMemo(() => {
        const featuresFromField = irrigationSystems
            .filter((system) => system.boundary)
            .map((system) => ({
                type: "Feature" as const,
                id: system.id,
                geometry: system.boundary,
                properties: { name: system.name },
            }));
        if (featuresFromField.length) {
            return {
                type: "FeatureCollection" as const,
                features: featuresFromField,
            };
        }
        return geometryQuery?.data?.irrigation_systems;
    }, [irrigationSystems, geometryQuery?.data?.irrigation_systems]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        const layerIds = [IRRIGATION_FILL_LAYER.id, IRRIGATION_LINE_LAYER.id];
        if (!irrigationBoundaries?.features?.length) {
            updateLayerVisibility(map, layerIds, false);
            return;
        }
        addUpdateGeojsonSource(map, IRRIGATION_SOURCE, {
            type: "geojson",
            data: irrigationBoundaries,
            tolerance: 0,
        });
        addUpdateGeojsonLayers(map, [
            IRRIGATION_FILL_LAYER,
            IRRIGATION_LINE_LAYER,
        ]);
        updateLayerVisibility(map, layerIds, true);
        moveLayersToTop(map, layerIds);
        return () => {
            updateLayerVisibility(map, layerIds, false);
        };
    }, [map, mapLoaded, irrigationBoundaries]);

    if (!fieldId || fieldQuery?.isLoading) {
        return (
            <div className="flex flex-col gap-2 w-full">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-row gap-2 items-center justify-end">
                <Button
                    size="sm"
                    color="primary"
                    as={Link}
                    to="/dashboard/fields/$fieldId/setup/irrigation-system/new"
                    params={
                        {
                            fieldId: String(fieldId),
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any
                    }
                    startContent={<FontAwesomeIcon icon={faPlus} />}
                >
                    {t("irrigation_system.add_irrigation_system", {
                        ns: "setup",
                    })}
                </Button>
            </div>
            {irrigationSystems.map((system, index) => {
                const typeKey =
                    IRRIGATION_SYSTEM_TYPE_I18N[
                        system.system_type ?? IrrigationSystemType.centerPivot
                    ];
                const pumpName = system.pump
                    ? getPumpName(system.pump, index)
                    : "";
                return (
                    <Card key={system.id} className="w-full hover:bg-gray-200">
                        <CardBody className="p-0">
                            <Link
                                to="/dashboard/fields/$fieldId/setup/irrigation-system/$irrigationSystemId"
                                params={{
                                    fieldId: String(fieldId),
                                    irrigationSystemId: String(system.id),
                                }}
                                className="flex flex-row gap-2 items-center justify-between p-3 text-inherit no-underline"
                            >
                                <div className="flex flex-col gap-1 min-w-0">
                                    <p className="text-md font-semibold truncate">
                                        {getSystemDisplayName(system, index, t)}
                                    </p>
                                    <div className="flex flex-row flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500">
                                        {system.acres != null && (
                                            <span>
                                                {Number(system.acres).toFixed(1)}{" "}
                                                {formatUnit("ac", "ha")}
                                            </span>
                                        )}
                                        {!!system.pivot_length && (
                                            <span>
                                                {t(
                                                    "irrigation_system.pivot_length",
                                                    { ns: "setup" },
                                                )}
                                                : {system.pivot_length}{" "}
                                                {formatUnit("ft", "m")}
                                            </span>
                                        )}
                                        {pumpName.trim() ? (
                                            <span>{pumpName}</span>
                                        ) : null}
                                    </div>
                                </div>
                                <Chip size="sm" variant="flat" color="primary">
                                    {t(
                                        `irrigation_system.system_types.${typeKey}`,
                                        { ns: "setup" },
                                    )}
                                </Chip>
                            </Link>
                        </CardBody>
                    </Card>
                );
            })}
            {!irrigationSystems.length && (
                <div className="flex flex-row gap-2 items-center justify-center py-4">
                    <div className="text-sm text-gray-500">
                        {t("irrigation_system.no_irrigation_systems", {
                            ns: "setup",
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default IrrigationSystems;
